import { useCallback } from "react";
import { useWatch } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useFormWithNotification } from "@covertree/hooks";
import FormFileField from "@covertree/ui/forms/FormFileField";
import DocumentItem from "@covertree/ui/content/DocumentItem";

import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { StepPath } from "@routing/paths";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "@consts/index";
import { selectClaimDocuments, submitClaimDocuments } from "@redux/slices";

import { ClaimsForm } from "../ClaimsForm";

interface UploadClaimDocumentsForm {
  files: File[];
}

export function UploadClaimDocuments() {
  const dispatch = useAppDispatch();
  const storedFiles = useAppSelector(selectClaimDocuments);

  const form = useFormWithNotification<UploadClaimDocumentsForm>({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: {
      files: storedFiles,
    },
  });

  const { control, setValue } = form;

  const validator = useCallback((file: File, currentFiles: File[]) => {
    const currentFileNames = currentFiles.map(({ name }) => name);

    if (currentFileNames.includes(file.name)) {
      return {
        code: "already-exists",
        message: `File with name " ${file.name} " already exists.`,
      };
    }

    return null;
  }, []);

  const handleSubmit = useCallback(
    (payload: UploadClaimDocumentsForm) => {
      dispatch(submitClaimDocuments(payload.files));
    },
    [dispatch]
  );

  const files = useWatch({ name: "files", control });

  const removeDocument = useCallback(
    (document: File) => {
      setValue(
        "files",
        files.filter((file) => document.name !== file.name)
      );
    },
    [files]
  );

  return (
    <ClaimsForm
      form={form}
      onSubmit={handleSubmit}
      navigateToStep={StepPath.WHO_IS_REPORTING_CLAIM}>
      <Grid xs={12} px={(theme) => theme.spacing(1.5)}>
        <Typography variant="h6">
          This can include police and fire reports, photos you took of the
          damage, repair estimates from a contractor, and receipts for your
          expenses.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormFileField
          name="files"
          accept={ACCEPTED_FILE_TYPES}
          maxFileSize={MAX_FILE_SIZE}
          validator={validator}
        />
      </Grid>
      {files?.map((file: File) => (
        <Grid item xs={12} key={file.name}>
          <DocumentItem
            name={file.name}
            actions={[
              {
                name: "Remove",
                onClick: () => removeDocument(file),
              },
            ]}
          />
        </Grid>
      ))}
    </ClaimsForm>
  );
}
